import { Box, Center, Text, VStack } from "@chakra-ui/layout";
import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Select,
} from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/spinner";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../Card/Card";
import { SearchInput } from "../SearchInput/SearchInput";
import { useSearchInputState } from "../../hooks/useSearchInputState";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import {
  MapPinFilledIcon,
  NotificationScheduleDateIcon,
  PlusIcon,
  TabItemSelectIcon,
} from "../../styles/icons";
import { NotificationDetailCard } from "../NotificationDetailCard/NotificationDetailCard";
import { useLayoutContext } from "../../context/LayoutContext";
import { CherryPayApi } from "../../api/models";
import { NotificationPlaceholder } from "./NotificationPlaceholder";
import { NotificationForm } from "../../forms/Notifications/NotificationForm";
import { useApiClient } from "../../hooks/useApiClient";
import {
  NotificationScheduleStatuses,
  NotificationSortByLabels,
  NotificationSortByOptions,
} from "./PushNotificationModels";
import DateUtil from "../../util/DateUtil";
import NotificationUtil from "../../util/NotificationUtil";
import { useToast } from "../../hooks/useToast";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useNotificationClone } from "../../hooks/useNotificationClone";
import { useNotificationDelete } from "../../hooks/useNotificationDelete";
import { useNotificationRevert } from "../../hooks/useNotificationRevert";
import { GuideDrawer } from "./GuideDrawer";
import { GuideFloatingActionButton } from "./GuideFloatingActionButton";
import { useUserContext } from "../../context/UserContext";
import { useStickyOverflowFix } from "../../hooks/useStickyOverflowFix";

export const PushNotifications = ({ businessId }: { businessId: string }) => {
  const { isSuperAdmin } = useUserContext();
  useStickyOverflowFix();
  const { query, debouncedQuery, onChange, isDebouncing, setQuery } =
    useSearchInputState("");
  const [selectedNotification, setSelectedNotification] =
    useState<CherryPayApi.CherryPassNotificationSchedule | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [notificationStatus, setNotificationStatus] =
    useState<string>("All Statuses");
  const [sortBy, setSortBy] = useState<string>("LastModifiedNewestFirst");
  const { isDesktop } = useLayoutContext();
  const [isCreateOrEditMode, setIsCreateOrEditMode] = useState<boolean>(false);
  const [isGuideDrawerOpen, setIsGuideDrawerOpen] = useState<boolean>(false);
  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const apiClient = useApiClient();

  const notificationStatusOptions: string[] = useMemo(() => {
    return ["All Statuses", ...NotificationScheduleStatuses];
  }, []);

  // Create query parameters
  const queryParams = useMemo(() => {
    const baseParams: {
      status?: CherryPayApi.NotificationScheduleStatus;
      sortBy?: CherryPayApi.NotificationScheduleSortByOption;
    } = {
      status: undefined,
      sortBy: undefined,
    };

    if (notificationStatus !== "All Statuses") {
      baseParams.status =
        notificationStatus as CherryPayApi.NotificationScheduleStatus;
    }

    baseParams.sortBy = sortBy as CherryPayApi.NotificationScheduleSortByOption;

    return baseParams;
  }, [notificationStatus, sortBy]);

  // Fetch notifications with appropriate filters
  const {
    items: notifications,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
    refresh,
  } = usePaginatedApiRequest(
    (apiClient) =>
      apiClient.searchPushNotificationsQuery(
        businessId,
        debouncedQuery,
        undefined,
        30,
        queryParams.status,
        queryParams.sortBy
      ),
    (apiClient, continuationToken) =>
      apiClient.searchPushNotificationsQuery(
        businessId,
        debouncedQuery,
        continuationToken,
        30,
        queryParams.status,
        queryParams.sortBy
      ),
    [businessId, debouncedQuery, queryParams]
  );

  // Update selectedNotification from latest data when notifications change
  useEffect(() => {
    if (selectedNotification && notifications) {
      const updatedNotification = notifications.find(
        (n) => n.Meta?.Id === selectedNotification.Meta?.Id
      );
      if (updatedNotification) {
        setSelectedNotification(updatedNotification);
      }
    }
  }, [notifications, selectedNotification]);

  // Scroll to top when switching between list and create/edit views
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [isCreateOrEditMode]);

  // Handle notification selection
  const handleNotificationClick = (
    notification: CherryPayApi.CherryPassNotificationSchedule
  ) => {
    setSelectedNotification(notification);

    if (!isDesktop) {
      setShowDetail(true);
    }
  };

  // Go back to list view (mobile only)
  const handleBackClick = () => {
    setShowDetail(false);
  };

  // Notification form handlers
  const handleNotificationSuccess = (
    notification: CherryPayApi.CherryPassNotificationSchedule,
    isEditing: boolean
  ) => {
    showSuccessToast(
      `Notification ${isEditing ? "updated" : "created"} successfully.`
    );

    // Reset filters so the latest notification appears in the list
    resetFilters();

    setIsCreateOrEditMode(false);
    // Force a refresh of the notifications list to see the new notification
    refresh();
    setSelectedNotification(notification);
  };

  const handleNotificationFailure = (message: string, isEditing: boolean) => {
    showErrorToast(
      `Failed to ${isEditing ? "update" : "create"} notification: ${message}`
    );
  };

  // Reset filters function
  const resetFilters = useCallback(() => {
    setQuery("");
    setNotificationStatus("All Statuses");
    setSortBy("LastModifiedNewestFirst");
  }, [setQuery, setNotificationStatus, setSortBy]);

  // Callback for post-clone operations
  const handlePostCloneOperations = useCallback(
    (clonedNotification: CherryPayApi.CherryPassNotificationSchedule) => {
      // Reset filters so the cloned notification can appear in the list
      resetFilters();

      // Refresh the list to include the new cloned notification
      refresh();

      // Set the cloned notification as selected and enter edit mode
      setSelectedNotification(clonedNotification);
      setIsCreateOrEditMode(true);
    },
    [resetFilters, refresh, setSelectedNotification, setIsCreateOrEditMode]
  );

  // Handle notification clone and delete with hooks
  const handleNotificationClone = useNotificationClone({
    businessId,
    onPostCloneOperations: handlePostCloneOperations,
  });

  const handleNotificationDelete = useNotificationDelete({
    businessId,
    selectedNotification,
    setSelectedNotification,
    refresh,
    showConfirmationDialog,
  });

  const handleNotificationRevert = useNotificationRevert({
    businessId,
    selectedNotification,
    setSelectedNotification,
    refresh,
    showConfirmationDialog,
    resetFilters,
  });

  // Render the notification list
  const renderNotificationList = () => {
    if (isLoading && (!notifications || notifications.length === 0)) {
      return (
        <Center p={8}>
          <Spinner />
        </Center>
      );
    }

    if (notifications?.length === 0) {
      return (
        <Center p={8}>
          <Text>
            {debouncedQuery === ""
              ? "No notifications found."
              : `No notifications found matching query "${debouncedQuery}".`}
          </Text>
        </Center>
      );
    }

    return (
      <VStack spacing={4} align="stretch" w="100%">
        {notifications?.map((notification) => {
          const isSelected =
            selectedNotification?.Meta?.Id === notification.Meta?.Id;
          const bgColor = isSelected
            ? "cherryBackground.selectedTabItem"
            : "white";
          const iconColor = isSelected
            ? "cherryUi.tabIconSelected"
            : "gray.500";
          const borderLeft = isSelected ? "4px solid" : "none";

          return (
            <Card
              key={notification.Meta?.Id}
              p={"10px 20px"}
              m={"0"}
              mt={"0 !important"}
              borderBottomWidth={"1px"}
              cursor="pointer"
              onClick={() => handleNotificationClick(notification)}
              bg={bgColor}
              borderLeft={borderLeft}
              borderLeftColor="cherryUi.600"
            >
              <HStack
                justify="space-between"
                align="start"
                alignItems="center"
                w="100%"
              >
                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold" pb={3} pt={1}>
                    {notification.DisplayName}
                  </Text>
                  <HStack spacing={2}>
                    <NotificationScheduleDateIcon
                      color="gray.500"
                      boxSize={4}
                    />
                    <Text fontSize="sm">
                      Send Date:{" "}
                      {DateUtil.getFormattedDateFromUtcToLocalTimezone(
                        notification.LocalScheduleSendDateTime
                      )}{" "}
                      {"    "}
                      {DateUtil.getFormattedTimeFromUtcToLocalTimezone(
                        notification.LocalScheduleSendDateTime
                      )}{" "}
                      {DateUtil.extractUtcOffsetFromTimezoneId(
                        notification.TimeZoneId
                      )}
                    </Text>
                  </HStack>
                  {Object.keys(
                    notification.LocationMessageOverride
                      ?.LocationMessageOverrides ?? {}
                  ).length > 0 && (
                    <HStack
                      bg="white"
                      border="1px solid"
                      borderColor="cherryBorder.100"
                      borderRadius="full"
                      px={3}
                      py={1}
                      spacing={1}
                    >
                      <MapPinFilledIcon color="gray.800" boxSize={4} />
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.800"
                        textTransform="uppercase"
                      >
                        Geo-Fence
                      </Text>
                    </HStack>
                  )}
                </VStack>

                <HStack align="center" spacing={2}>
                  <Badge
                    px={3}
                    py={1}
                    borderRadius="full"
                    background={NotificationUtil.getStatusColorScheme(
                      notification.NotificationScheduleStatus
                    )}
                    fontSize="xs"
                    fontWeight="bold"
                    color={"white"}
                    textTransform="uppercase"
                  >
                    {notification.NotificationScheduleStatus}
                  </Badge>
                  <TabItemSelectIcon color={iconColor} fontSize={"20px"} />
                </HStack>
              </HStack>
            </Card>
          );
        })}
        {fetchNextPage && (
          <Box pl={4}>
            {!isLoadingPage ? (
              <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                Load more...
              </Button>
            ) : (
              <Spinner size="sm" />
            )}
          </Box>
        )}
      </VStack>
    );
  };

  // Create or Edit mode - render the notification form
  if (isCreateOrEditMode) {
    return (
      <>
        <NotificationForm
          apiClient={apiClient}
          businessId={businessId}
          allowSilentEmptyPassUpdate={isSuperAdmin}
          notification={selectedNotification}
          notificationName={
            selectedNotification
              ? selectedNotification.DisplayName
              : "New Push Notification"
          }
          onCancel={() => setIsCreateOrEditMode(false)}
          onSuccess={handleNotificationSuccess}
          onFailure={handleNotificationFailure}
        />
        <GuideFloatingActionButton onClick={() => setIsGuideDrawerOpen(true)} />
        <GuideDrawer
          isOpen={isGuideDrawerOpen}
          onClose={() => setIsGuideDrawerOpen(false)}
        />
      </>
    );
  }

  // Mobile view with back button when showing detail
  if (!isDesktop && showDetail && selectedNotification) {
    return (
      <VStack
        width="100%"
        spacing={4}
        align="stretch"
        background={"cherryBackground.placeholder"}
        minH={"100vh"}
      >
        <HStack width="100%" pl={"20px"} pt={"20px"}>
          <Link
            to="#"
            onClick={handleBackClick}
            style={{ alignSelf: "flex-start", marginBottom: "10px" }}
          >
            &lt; Back to All Notifications
          </Link>
        </HStack>
        <Box
          height="100%"
          m={"24px !important"}
          p={10}
          borderLeft={"1px solid"}
          borderColor="gray.200"
          background={"white"}
          boxShadow={"md"}
        >
          <NotificationDetailCard
            notification={selectedNotification}
            onEdit={(notification) => {
              setSelectedNotification(notification);
              setIsCreateOrEditMode(true);
              setShowDetail(false);
            }}
            onClone={handleNotificationClone}
            onDelete={handleNotificationDelete}
            onRevert={handleNotificationRevert}
          />
        </Box>
      </VStack>
    );
  }

  return (
    <VStack width="100%" spacing={4}>
      <Card width="100%" padding={"0"}>
        <VStack width="100%" spacing={4} alignItems="start">
          <HStack
            width="100%"
            paddingBottom={"10px"}
            borderBottomWidth="1px"
            borderColor={"cherryBorder.100"}
            sx={{ gap: "10px" }}
          >
            <Box width="250px" marginLeft={"20px"}>
              <SearchInput
                value={query}
                onChange={onChange}
                isLoading={isLoading || isDebouncing}
                placeholder="Search Notifications"
                setQuery={setQuery}
              />
            </Box>
            <Box width="200px">
              <Select
                value={notificationStatus}
                onChange={(e) => setNotificationStatus(e.target.value)}
              >
                {notificationStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            </Box>
            <Box width="300px">
              <FormControl display={"flex"} alignItems={"center"}>
                <FormLabel width={"80px"} mb={"0"} mr={"0"}>
                  Sort by
                </FormLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  {NotificationSortByOptions.map((option) => (
                    <option key={option} value={option}>
                      {option in NotificationSortByLabels
                        ? NotificationSortByLabels[
                            option as keyof typeof NotificationSortByLabels
                          ]
                        : option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              leftIcon={<PlusIcon />}
              onClick={() => {
                setIsCreateOrEditMode(true);
                setSelectedNotification(null);
                setShowDetail(false);
              }}
              colorScheme="cherryButton"
              marginInlineEnd={"30px !important"}
              marginLeft={"auto !important"}
              whiteSpace="normal"
              height="auto"
              py={2}
            >
              New Notification
            </Button>
          </HStack>
          <Flex
            w="100%"
            direction={isDesktop ? "row" : "column"}
            marginTop={"0px !important"}
            background={"cherryBackground.placeholder"}
          >
            <Box
              w={isDesktop ? "30%" : "100%"}
              pr={0}
              background={"white"}
              minH={"600px"}
            >
              {renderNotificationList()}
            </Box>

            {isDesktop && (
              <Box
                w="70%"
                height="100%"
                m={8}
                p={10}
                borderLeft={selectedNotification ? "1px solid" : "none"}
                borderColor="gray.200"
                background={selectedNotification ? "white" : undefined}
                boxShadow={selectedNotification ? "md" : undefined}
                position="sticky"
                top={"20px"}
                alignSelf="flex-start"
              >
                {selectedNotification ? (
                  <NotificationDetailCard
                    notification={selectedNotification}
                    onEdit={(notification) => {
                      setSelectedNotification(notification);
                      setIsCreateOrEditMode(true);
                    }}
                    onClone={handleNotificationClone}
                    onDelete={handleNotificationDelete}
                    onRevert={handleNotificationRevert}
                  />
                ) : (
                  <NotificationPlaceholder />
                )}
              </Box>
            )}
          </Flex>

          {error != null && (
            <ErrorMessage>
              An error was encountered while searching notifications.
            </ErrorMessage>
          )}
        </VStack>
      </Card>
      {confirmationElement}
      <GuideFloatingActionButton onClick={() => setIsGuideDrawerOpen(true)} />
      <GuideDrawer
        isOpen={isGuideDrawerOpen}
        onClose={() => setIsGuideDrawerOpen(false)}
      />
    </VStack>
  );
};
